/** @format */
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { SessionStorageService } from 'sis-common';
import { TimelineControls } from '../timing.types';

const STORAGE_KEY = 'timeline.controls';
type ControlName = keyof TimelineControls;

const DEFAULT_CONTROLS: TimelineControls = { showEmptySummerPeriods: false, showPastStudyTerms: false, useHorizontalLayout: false };

@Injectable({ providedIn: 'root' })
export class TimelineControlsService {
    private readonly sessionStorageService = inject(SessionStorageService);

    private readonly timelineControls$ = new BehaviorSubject<TimelineControls>({
        ...DEFAULT_CONTROLS,
        ...this.sessionStorageService.getValueOrDefault(STORAGE_KEY, {}),
    });

    readonly showEmptySummerPeriods$: Observable<boolean> = this.readControl('showEmptySummerPeriods');
    readonly showPastStudyTerms$: Observable<boolean> = this.readControl('showPastStudyTerms');
    readonly useHorizontalLayout$: Observable<boolean> = this.readControl('useHorizontalLayout');

    setShowEmptySummerPeriods(value: boolean): void {
        this.updateControl('showEmptySummerPeriods', value);
    }

    setShowPastStudyTerms(value: boolean): void {
        this.updateControl('showPastStudyTerms', value);
    }

    setUseHorizontalLayout(value: boolean): void {
        this.updateControl('useHorizontalLayout', value);
    }

    private readControl(controlName: ControlName): Observable<boolean> {
        return this.timelineControls$.pipe(
            map(controls => controls[controlName]),
            distinctUntilChanged(),
        );
    }

    private updateControl(controlName: ControlName, value: boolean): void {
        const updatedControls = { ...this.timelineControls$.getValue(), [controlName]: value };
        this.sessionStorageService.setValue(STORAGE_KEY, updatedControls);
        this.timelineControls$.next(updatedControls);
    }
}
